// src/ManagerDashboard.js
import ScrollToTop from './ScrollToTop';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import CatalogueManager from './CatalogueManager';
import CommissionsManager from './CommissionsManager';
import DimensionnementRulesManager from './DimensionnementRulesManager';
import RapportMensuelManager from './RapportMensuelManager';
import SuiviInstallations from './SuiviInstallations';
import DashboardFinancier from './DashboardFinancier';
import { useAuth } from './AuthContext';
import { motion } from 'framer-motion';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts';
import { Sparklines, SparklinesLine } from 'react-sparklines';
import { TrendingUp, TrendingDown, FileText, DollarSign, Settings, Save, Zap, User } from 'lucide-react';
import ClientsPanel from './ClientsPanel';
import EquipeManager from './EquipeManager';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

const StatCard = ({ icon: Icon, title, value, sub, color, spark, trend }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
    style={{ background: 'white', borderRadius: '14px', padding: '20px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}` }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '6px' }}>{title}</div>
        <div style={{ fontSize: '26px', fontWeight: 700, color: '#111827' }}>{value}</div>
        {sub && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{sub}</div>}
        {trend !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px',
            color: trend >= 0 ? '#10b981' : '#ef4444', fontSize: '13px', fontWeight: 600 }}>
            {trend >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {Math.abs(trend)}% vs mois dernier
          </div>
        )}
      </div>
      <div style={{ padding: '10px', borderRadius: '10px', background: color + '20' }}>
        <Icon size={22} color={color} />
      </div>
    </div>
    {spark && spark.length > 0 && (
      <div style={{ marginTop: '12px' }}>
        <Sparklines data={spark} height={30}>
          <SparklinesLine color={color} style={{ fill: 'none' }} />
        </Sparklines>
      </div>
    )}
  </motion.div>
);

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true);
  const [period, setPeriod] = useState('month');

  const [tarifKwh, setTarifKwh] = useState(200);
  const [tarifKwhEdit, setTarifKwhEdit] = useState('200');
  const [savingTarif, setSavingTarif] = useState(false);
  const [tarifSaved, setTarifSaved] = useState(false);
  const [prixGasoil, setPrixGasoil] = useState(680);
  const [prixGasoilEdit, setPrixGasoilEdit] = useState('680');
  const [savingGasoil, setSavingGasoil] = useState(false);
  const [gasoilSaved, setGasoilSaved] = useState(false);
  const [prixSeneau, setPrixSeneau] = useState(424);
  const [prixSeneauEdit, setPrixSeneauEdit] = useState('424');
  const [savingSeneau, setSavingSeneau] = useState(false);
  const [seneauSaved, setSeneauSaved] = useState(false);
  const [tauxUsd, setTauxUsd] = useState(620);
  const [tauxUsdEdit, setTauxUsdEdit] = useState('620');
  const [savingTauxUsd, setSavingTauxUsd] = useState(false);
  const [tauxUsdSaved, setTauxUsdSaved] = useState(false);
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('mgr_tab') || '');
  const switchTab = (tab) => { const next = activeTab === tab ? '' : tab; setActiveTab(next); if(next){localStorage.setItem('mgr_tab',next);setTimeout(()=>window.scrollTo({top:0,behavior:'smooth'}),80);}else{localStorage.removeItem('mgr_tab');} };
  const showConfig = activeTab === 'showConfig';
  const showCommissions = activeTab === 'showCommissions';
  const showDimRules = activeTab === 'showDimRules';
  const showRapport = activeTab === 'showRapport';
  const showCatalogue = activeTab === 'showCatalogue';
  const showSuivi = activeTab === 'showSuivi';
  const showFinancier = activeTab === 'showFinancier';
  const showOutils = activeTab === 'showOutils';

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: quotesData }, { data: profilesData }, { data: settingsData }] = await Promise.all([
      supabase.from('quotes').select('*').order('created_at', { ascending: true }),
      supabase.from('profiles').select('*'),
      supabase.from('settings').select('*'),
    ]);
    setQuotes(quotesData || []);
    setProfiles(profilesData || []);
    const tarifSetting = (settingsData || []).find(s => s.cle === 'tarif_kwh');
    const gasoilSetting = (settingsData || []).find(s => s.cle === 'prix_gasoil');
    if (gasoilSetting) { const v = parseFloat(gasoilSetting.valeur); if (!isNaN(v)) { setPrixGasoil(v); setPrixGasoilEdit(String(v)); } }
    const seneauSetting = (settingsData || []).find(s => s.cle === 'prix_seneau_m3');
    if (seneauSetting) { const v = parseFloat(seneauSetting.valeur); if (!isNaN(v)) { setPrixSeneau(v); setPrixSeneauEdit(String(v)); } }
    const tauxUsdSetting = (settingsData || []).find(s => s.cle === 'taux_usd_fcfa');
    if (tauxUsdSetting) { const v = parseFloat(tauxUsdSetting.valeur); if (!isNaN(v)) { setTauxUsd(v); setTauxUsdEdit(String(v)); } }
    if (tarifSetting) { const val = parseFloat(tarifSetting.valeur) || 200; setTarifKwh(val); setTarifKwhEdit(String(val)); }
    setLoading(false);
    setFirstLoad(false);
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => { fetchData(); }, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleSaveTarif = async () => {
    const val = parseFloat(tarifKwhEdit);
    if (!val || val <= 0) return;
    setSavingTarif(true);
    await supabase.from('settings').upsert({ cle: 'tarif_kwh', valeur: String(val), label: 'Tarif SENELEC (FCFA/kWh)' }, { onConflict: 'cle' });
    setTarifKwh(val); setSavingTarif(false); setTarifSaved(true);
    setTimeout(() => setTarifSaved(false), 2000);
  };

  const handleSaveGasoil = async () => {
    const val = parseFloat(prixGasoilEdit);
    if (isNaN(val) || val < 100) return;
    setSavingGasoil(true);
    await supabase.from('settings').upsert({ cle: 'prix_gasoil', valeur: String(val), label: 'Prix gasoil (FCFA/litre)' }, { onConflict: 'cle' });
    setPrixGasoil(val); setSavingGasoil(false); setGasoilSaved(true);
    setTimeout(() => setGasoilSaved(false), 3000);
  };

  const handleSaveSeneau = async () => {
    const val = parseFloat(prixSeneauEdit);
    if (isNaN(val) || val < 100) return;
    setSavingSeneau(true);
    await supabase.from('settings').upsert({ cle: 'prix_seneau_m3', valeur: String(val), label: 'Prix eau SENEAU (FCFA/m3)' }, { onConflict: 'cle' });
    setPrixSeneau(val); setSavingSeneau(false); setSeneauSaved(true);
    setTimeout(() => setSeneauSaved(false), 3000);
  };

  const handleSaveTauxUsd = async () => {
    const val = parseFloat(tauxUsdEdit);
    if (isNaN(val) || val < 100) return;
    setSavingTauxUsd(true);
    await supabase.from('settings').upsert({ cle: 'taux_usd_fcfa', valeur: String(val), label: 'Taux de change USD/FCFA' }, { onConflict: 'cle' });
    setTauxUsd(val); setSavingTauxUsd(false); setTauxUsdSaved(true);
    setTimeout(() => setTauxUsdSaved(false), 3000);
  };

  const now = new Date();
  const filteredQuotes = quotes.filter(q => {
    if (q.statut === 'archive') return false;
    if (period === 'all') return true;
    const d = new Date(q.date);
    if (period === 'month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    if (period === 'year') return d.getFullYear() === now.getFullYear();
    return true;
  });

  const totalCA = filteredQuotes.filter(q => q.statut === 'gagne').reduce((s, q) => s + (q.total_with_vat || 0), 0);
  const totalDevis = filteredQuotes.length;
  const totalGagne = filteredQuotes.filter(q => q.statut === 'gagne').length;
  const totalPerdu = filteredQuotes.filter(q => q.statut === 'perdu').length;
  const totalEnCours = filteredQuotes.filter(q => q.statut === 'en_cours').length;
  const tauxConversion = totalDevis > 0 ? Math.round((totalGagne / totalDevis) * 100) : 0;
  const panierMoyen = totalGagne > 0 ? totalCA / totalGagne : 0;

  const caParMois = (() => {
    if (period === 'month') {
      return ['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4'].map((sem, i) => {
        const val = quotes.filter(q => {
          const d = new Date(q.date);
          const week = Math.floor((d.getDate() - 1) / 7);
          return q.statut === 'gagne' && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear() && week === i;
        }).reduce((s, q) => s + (q.total_with_vat || 0), 0);
        return { name: sem, CA: Math.round(val / 1000) };
      });
    }
    return MONTHS.map((m, i) => {
      const val = quotes.filter(q => {
        const d = new Date(q.date);
        return q.statut === 'gagne' && d.getMonth() === i && (period === 'all' || d.getFullYear() === now.getFullYear());
      }).reduce((s, q) => s + (q.total_with_vat || 0), 0);
      return { name: m, CA: Math.round(val / 1000) };
    });
  })();

  const statutData = [
    { name: 'Gagné', value: totalGagne },
    { name: 'En cours', value: totalEnCours },
    { name: 'Perdu', value: totalPerdu },
  ].filter(d => d.value > 0);

  const commerciaux = profiles.filter(p => p.role === 'commerciale').map(p => {
    const myQuotes = filteredQuotes.filter(q => q.user_id === p.id);
    const myCA = myQuotes.filter(q => q.statut === 'gagne').reduce((s, q) => s + (q.total_with_vat || 0), 0);
    const myGagne = myQuotes.filter(q => q.statut === 'gagne').length;
    const isOnline = p.last_seen && (new Date() - new Date(p.last_seen)) < 3 * 60 * 1000;
    return { nom: p.nom, devis: myQuotes.length, gagne: myGagne, ca: myCA, isOnline, lastSeen: p.last_seen };
  }).sort((a, b) => b.ca - a.ca);

  const produits = {};
  filteredQuotes.filter(q => q.statut === 'gagne').forEach(q => {
    (q.items || []).forEach(item => {
      const key = item.description?.substring(0, 40) || 'Produit';
      if (!produits[key]) produits[key] = { qte: 0, ca: 0 };
      produits[key].qte += (item.quantity || 0);
      produits[key].ca += (item.quantity || 0) * (item.unitPrice || 0);
    });
  });
  const topProduits = Object.entries(produits).map(([name, v]) => ({ name, ...v })).sort((a, b) => b.ca - a.ca).slice(0, 5);
  const sparkCA = caParMois.map(m => m.CA);
  const handleSignOut = async () => { await signOut(); navigate('/login'); };

  if (loading && firstLoad) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f4f8' }}>
      <div style={{ color: '#6b7280', fontSize: '18px' }}>Chargement du tableau de bord...</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4f8', fontFamily: 'Segoe UI, sans-serif', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1e3a8a, #1e40af)', padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={() => navigate('/')}
            onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.28)'; }}
            onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.15)'; }}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', transition: 'all 0.15s' }}>
            + Nouveau devis
          </button>
          <h1 style={{ color: 'white', fontSize: '20px', fontWeight: 700, margin: 0 }}>Console Manager</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <select value={period} onChange={e => setPeriod(e.target.value)}
            style={{ borderRadius: '8px', padding: '8px 12px', border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            <option value="month">Ce mois</option>
            <option value="year">Cette année</option>
            <option value="all">Tout</option>
          </select>
          <div style={{ position: 'relative' }}>
            <button onClick={() => switchTab('showOutils')}
              onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.28)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = showOutils ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)'; }}
              style={{ background: showOutils ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              Outils {showOutils ? '▲' : '▼'}
            </button>
            {showOutils && (
              <div style={{ position: 'absolute', top: '42px', right: 0, background: 'white', borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 200, minWidth: '180px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                {[
                  { label: 'Réglages', icon: '⚙️', action: () => switchTab('showConfig') },
                  { label: 'Catalogue', icon: '📦', action: () => switchTab('showCatalogue') },
                  { label: 'Suivi Installations', icon: '🔧', action: () => switchTab('showSuivi') },
                  { label: 'Dashboard Financier', icon: '📊', action: () => switchTab('showFinancier') },
                  { label: 'Commissions', icon: '💰', action: () => switchTab('showCommissions') },
                  { label: 'Règles dimensionnement', icon: '⚡', action: () => switchTab('showDimRules') },
                  { label: 'Rapport mensuel', icon: '📧', action: () => switchTab('showRapport') },
                ].map((item, i) => (
                  <button key={i} onClick={item.action}
                    style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: '13px', cursor: 'pointer', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: i < 3 ? '1px solid #f3f4f6' : 'none' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f0f4ff'}
                    onMouseLeave={e => e.currentTarget.style.background='none'}>
                    {item.icon} {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => navigate('/quote-dashboard')}
            onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.28)'; }}
            onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.15)'; }}
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            📋 Tous les devis
          </button>
          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <User size={13} /><span>{profile?.nom}</span>
          </span>
          <button onClick={handleSignOut}
            onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.28)'; }}
            onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.15)'; }}
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            Déconnexion
          </button>
        </div>
      </div>

      <div style={{ padding: '24px 32px', maxWidth: '1400px', margin: '0 auto', width: '100%' }}>

        {/* ── Panneau Configuration ── */}
        {showConfig && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: '24px', border: '2px solid #e0e7ff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '16px', color: '#1e40af', marginBottom: '20px' }}>
              <Settings size={18} /> Configuration — Paramètres de l'application
            </div>

            {/* Grille réglages 2 colonnes */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: '20px' }}>

              {/* Tarif SENELEC */}
              <div style={{ background: '#eff6ff', borderRadius: '12px', padding: '20px', border: '1px solid #bfdbfe' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '14px', color: '#1e40af', marginBottom: '6px' }}>
                  <Zap size={16} /> Tarif SENELEC (FCFA/kWh)
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                  Utilisé pour calculer le ROI et les économies dans les devis PV.
                  Tarif terrain : <strong>200 FCFA/kWh</strong> (1 000 FCFA = 5.6 kWh sur Woyofal).
                  Ajuster selon le profil de consommation du client (tarif progressif SENELEC).
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input type="number" value={tarifKwhEdit} min="100" max="500" step="10"
                    onChange={e => setTarifKwhEdit(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSaveTarif()}
                    style={{ flex: 1, border: '2px solid #bfdbfe', borderRadius: '8px', padding: '10px 14px', fontSize: '16px', fontWeight: 700, outline: 'none', textAlign: 'center' }}
                    onFocus={e => e.target.style.borderColor='#3b82f6'}
                    onBlur={e => e.target.style.borderColor='#bfdbfe'} />
                  <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>FCFA/kWh</span>
                  <button onClick={handleSaveTarif} disabled={savingTarif}
                    onMouseEnter={e => { if (!savingTarif) e.currentTarget.style.background='#1e3a8a'; }}
                    onMouseLeave={e => { if (!savingTarif) e.currentTarget.style.background = tarifSaved ? '#10b981' : '#1e40af'; }}
                    style={{ background: tarifSaved ? '#10b981' : '#1e40af', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: 600, cursor: savingTarif ? 'default' : 'pointer', transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}>
                    {tarifSaved ? '✅ Sauvegardé' : savingTarif ? '...' : <><Save size={13} /> Sauvegarder</>}
                  </button>
                </div>
                <div style={{ marginTop: '10px', fontSize: '11px', color: '#9ca3af' }}>
                  Valeur actuelle : <strong style={{ color: '#1e40af' }}>{tarifKwh} FCFA/kWh</strong> — Économies annuelles = production kWh × tarif
                </div>
              </div>

              {/* Prix gasoil */}
              <div style={{ background: '#fefce8', borderRadius: '12px', padding: '20px', border: '1px solid #fde68a' }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#92400e', marginBottom: '6px' }}>
                  {String.fromCharCode(9981)} Prix du gasoil (FCFA/litre)
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                  Utilis{String.fromCharCode(233)} pour le ROI des pompes solaires (remplacement groupe {String.fromCharCode(233)}lectrog{String.fromCharCode(232)}ne).
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input type='number' value={prixGasoilEdit} min='100' max='2000' step='10'
                    onChange={e => setPrixGasoilEdit(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSaveGasoil()}
                    style={{ flex: 1, border: '2px solid #fde68a', borderRadius: '8px', padding: '10px 14px', fontSize: '16px', fontWeight: 700, outline: 'none', textAlign: 'center' }} />
                  <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 600 }}>FCFA/litre</span>
                  <button onClick={handleSaveGasoil} disabled={savingGasoil}
                    style={{ background: gasoilSaved ? '#10b981' : '#d97706', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                    {gasoilSaved ? 'Sauvegard' + String.fromCharCode(233) : savingGasoil ? '...' : 'Sauvegarder'}
                  </button>
                </div>
                <div style={{ marginTop: '10px', fontSize: '11px', color: '#9ca3af' }}>
                  Valeur actuelle : <strong style={{ color: '#d97706' }}>{prixGasoil} FCFA/litre</strong>
                </div>
              </div>

              {/* Prix SEN'EAU */}
              <div style={{ background: '#f0f9ff', borderRadius: '12px', padding: '20px', border: '1px solid #bae6fd' }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#0369a1', marginBottom: '6px' }}>
                  {String.fromCharCode(128167)} Prix eau SEN{String.fromCharCode(39)}EAU (FCFA/m3)
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                  Utilis{String.fromCharCode(233)} pour le ROI des pompes solaires (remplacement abonnement SEN{String.fromCharCode(39)}EAU).
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input type='number' value={prixSeneauEdit} min='100' max='2000' step='10'
                    onChange={e => setPrixSeneauEdit(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSaveSeneau()}
                    style={{ flex: 1, border: '2px solid #bae6fd', borderRadius: '8px', padding: '10px 14px', fontSize: '16px', fontWeight: 700, outline: 'none', textAlign: 'center' }} />
                  <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 600 }}>FCFA/m3</span>
                  <button onClick={handleSaveSeneau} disabled={savingSeneau}
                    style={{ background: seneauSaved ? '#10b981' : '#0369a1', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                    {seneauSaved ? 'Sauvegard' + String.fromCharCode(233) : savingSeneau ? '...' : 'Sauvegarder'}
                  </button>
                </div>
                <div style={{ marginTop: '10px', fontSize: '11px', color: '#9ca3af' }}>
                  Valeur actuelle : <strong style={{ color: '#0369a1' }}>{prixSeneau} FCFA/m3</strong>
                </div>
              </div>

              {/* Taux USD/FCFA */}
              <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: '20px', border: '1px solid #a7f3d0' }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#065f46', marginBottom: '6px' }}>
                  💱 Taux de change USD/FCFA
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                  Utilis{String.fromCharCode(233)} pour calculer les prix de vente des {String.fromCharCode(233)}quipements import{String.fromCharCode(233)}s (climatiseurs mobiles, etc.).
                  Formule : prix fabricant USD × 2 × 1.3 × taux.
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input type='number' value={tauxUsdEdit} min='500' max='1000' step='5'
                    onChange={e => setTauxUsdEdit(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSaveTauxUsd()}
                    style={{ flex: 1, border: '2px solid #a7f3d0', borderRadius: '8px', padding: '10px 14px', fontSize: '16px', fontWeight: 700, outline: 'none', textAlign: 'center' }} />
                  <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 600 }}>FCFA/USD</span>
                  <button onClick={handleSaveTauxUsd} disabled={savingTauxUsd}
                    style={{ background: tauxUsdSaved ? '#10b981' : '#059669', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                    {tauxUsdSaved ? 'Sauvegard' + String.fromCharCode(233) : savingTauxUsd ? '...' : 'Sauvegarder'}
                  </button>
                </div>
                <div style={{ marginTop: '10px', fontSize: '11px', color: '#9ca3af' }}>
                  Valeur actuelle : <strong style={{ color: '#059669' }}>{tauxUsd} FCFA/USD</strong>
                </div>
              </div>

              {/* Templates relance */}
              <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: '20px', border: '1px solid #bbf7d0' }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#065f46', marginBottom: '6px' }}>
                  📧 Modèles email de relance
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                  5 modèles configurables : J+2, J+7, J+30, J+60, J+90.
                  Variables disponibles : [Prenom], [NumeroDevis], [Commerciale].
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {[2, 7, 30, 60, 90].map(j => (
                    <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', background: 'white', borderRadius: '6px', border: '1px solid #d1fae5', fontSize: '12px', color: '#374151' }}>
                      <span style={{ background: '#d1fae5', color: '#065f46', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>J+{j}</span>
                      <span style={{ color: '#9ca3af' }}>Modèle actif</span>
                      <span style={{ marginLeft: 'auto', color: '#10b981', fontSize: '11px' }}>✅ Configuré</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: '10px', fontSize: '11px', color: '#9ca3af' }}>
                  Pour modifier les textes : Supabase → Table email_templates
                </div>
              </div>

            </div>{/* fin grille réglages */}

            {/* Equipe & Accès — pleine largeur */}
            <div style={{ background: '#faf5ff', borderRadius: '12px', padding: '20px', border: '1px solid #e9d5ff' }}>
              <div style={{ fontWeight: 700, fontSize: '14px', color: '#6d28d9', marginBottom: '6px' }}>
                👥 Équipe &amp; Accès
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '14px' }}>
                Ajouter, désactiver ou supprimer des membres — promouvoir en manager — envoyer les identifiants.
              </div>
              <EquipeManager />
            </div>

          </motion.div>
        )}

        {/* ── Panneau Commissions ── */}
        {showCommissions && (
          <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: '24px', border: '2px solid #fef3c7' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '16px', color: '#92400e', marginBottom: '20px' }}>
              💰 Équipe &amp; Commissions
            </div>
            <CommissionsManager />
          </div>
        )}

        {/* Panneau Rapport mensuel */}
        {showRapport && (
          <div style={{ background: 'white', borderRadius: '12px', margin: '16px 0', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' }}>
            <RapportMensuelManager />
          </div>
        )}

        {/* Panneau Règles dimensionnement */}
        {showDimRules && (
          <div style={{ background: 'white', borderRadius: '12px', margin: '16px 0', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' }}>
            <DimensionnementRulesManager />
          </div>
        )}

        {/* Panneau Catalogue */}
        {showCatalogue && (
          <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: '24px', border: '2px solid #e0e7ff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '16px', color: '#1e40af', marginBottom: '20px' }}>
              Catalogue produits — Prix, activation, vedette
            </div>
            <CatalogueManager />
          </div>
        )}

        {/* Panneau Suivi Installations */}
        {showSuivi && (
          <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: '24px', border: '2px solid #e0e7ff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '16px', color: '#1e40af', marginBottom: '20px' }}>
              Suivi Installations post-vente
            </div>
            <SuiviInstallations />
          </div>
        )}

        {/* Panneau Dashboard Financier */}
        {showFinancier && (
          <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: '24px', border: '2px solid #e0e7ff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '16px', color: '#1e40af', marginBottom: '20px' }}>
              Dashboard Financier
            </div>
            <DashboardFinancier quotes={quotes} />
          </div>
        )}

        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <StatCard icon={DollarSign} title="Chiffre d'affaires" color="#3b82f6" value={fmt(totalCA) + ' FCFA'} spark={sparkCA} />
          <StatCard icon={FileText} title="Total devis" color="#8b5cf6" value={totalDevis} sub={`${totalGagne} gagnés · ${totalEnCours} en cours · ${totalPerdu} perdus`} />
          <StatCard icon={TrendingUp} title="Taux de conversion" color="#10b981" value={tauxConversion + '%'} sub={`${totalGagne} devis sur ${totalDevis}`} />
          <StatCard icon={DollarSign} title="Panier moyen" color="#f59e0b" value={fmt(panierMoyen) + ' FCFA'} sub="Sur les devis gagnés" />
        </div>

        {/* Graphiques ligne 1 */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '20px' }}>
              📈 {period === 'month' ? `CA semaines — ${now.toLocaleString('fr-FR', {month: 'long'})} ${now.getFullYear()}` : period === 'year' ? `CA mensuel ${now.getFullYear()}` : 'CA total — toutes périodes'} (milliers FCFA)
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={caParMois}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => [fmt(v * 1000) + ' FCFA', 'CA']} />
                <Bar dataKey="CA" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '20px' }}>📊 Répartition des devis</h3>
            {statutData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={statutData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} dataKey="value" labelLine={false}>
                      {statutData.map((_, i) => <Cell key={i} fill={['#10b981', '#f59e0b', '#ef4444'][i]} />)}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap', marginTop: '12px' }}>
                  {statutData.map((entry, i) => {
                    const total = statutData.reduce((s, d) => s + d.value, 0);
                    const pct = total > 0 ? ((entry.value / total) * 100).toFixed(0) : 0;
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: ['#10b981', '#f59e0b', '#ef4444'][i], flexShrink: 0 }} />
                        <span style={{ fontWeight: 500 }}>{entry.name}</span>
                        <span style={{ color: '#6b7280' }}>({entry.value} — {pct}%)</span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: '60px' }}>Aucun devis</div>
            )}
          </motion.div>
        </div>

        {/* Graphiques ligne 2 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
            style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '20px' }}>🏆 Classement des commercial(e)s</h3>
            {commerciaux.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0' }}>Aucun(e) commercial(e) enregistré(e)</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {commerciaux.map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: i === 0 ? '#fbbf24' : i === 1 ? '#9ca3af' : '#cd7c3f', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '13px', fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
                            className='tooltip-container'>
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                              background: c.isOnline ? '#10b981' : '#d1d5db',
                              boxShadow: c.isOnline ? '0 0 0 2px #d1fae5' : 'none',
                              display: 'inline-block', cursor: 'help' }}
                              title={(() => {
                                if (c.isOnline) return 'En ligne';
                                if (!c.lastSeen) return 'Jamais connecté(e)';
                                const d = new Date(c.lastSeen);
                                const now = new Date();
                                const diffMs = now - d;
                                const diffMin = Math.floor(diffMs / 60000);
                                const diffH = Math.floor(diffMs / 3600000);
                                const diffJ = Math.floor(diffMs / 86400000);
                                const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                                const aujourdhui = new Date(); aujourdhui.setHours(0,0,0,0);
                                const hier = new Date(aujourdhui); hier.setDate(hier.getDate() - 1);
                                const dJour = new Date(d); dJour.setHours(0,0,0,0);
                                if (diffMin < 1) return 'Vu à l instant';
                                if (diffMin < 60) return 'Vu il y a ' + diffMin + ' min';
                                if (dJour.getTime() === aujourdhui.getTime()) return 'Vu aujourd hui à ' + heure;
                                if (dJour.getTime() === hier.getTime()) return 'Vu hier à ' + heure;
                                if (diffJ < 7) return 'Vu ' + d.toLocaleDateString('fr-FR', { weekday: 'long' }) + ' à ' + heure;
                                return 'Vu le ' + d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) + ' à ' + heure;
                              })()}
                            />
                          </span>
                          {c.nom}
                        </span>
                        <span style={{ fontSize: '13px', color: '#6b7280' }}>{c.gagne}/{c.devis} devis</span>
                      </div>
                      <div style={{ marginTop: '4px', background: '#f3f4f6', borderRadius: '999px', height: '6px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: '999px', background: '#3b82f6', width: `${commerciaux[0]?.ca > 0 ? (c.ca / commerciaux[0].ca) * 100 : 0}%`, transition: 'width 1s ease' }} />
                      </div>
                      <div style={{ fontSize: '12px', color: '#3b82f6', marginTop: '2px', fontWeight: 600 }}>{fmt(c.ca)} FCFA</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '20px' }}>📦 Top 5 produits vendus</h3>
            {topProduits.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0' }}>Aucun produit vendu</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={topProduits} layout="vertical">
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => fmt(v / 1000) + 'k'} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={v => [fmt(v) + ' FCFA', 'CA']} />
                  <Bar dataKey="ca" radius={[0, 4, 4, 0]}>
                    {topProduits.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </motion.div>
        </div>

        {/* Tableau devis récents */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
          style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>📋 Derniers devis</h3>
            <button onClick={() => navigate('/quote-dashboard')}
              onMouseEnter={e => { e.currentTarget.style.background='#dbeafe'; e.currentTarget.style.color='#1e3a8a'; }}
              onMouseLeave={e => { e.currentTarget.style.background='#eff6ff'; e.currentTarget.style.color='#1e40af'; }}
              style={{ background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '6px 14px', fontSize: '13px', cursor: 'pointer', transition: 'all 0.15s', fontWeight: 500 }}>
              Voir tous les devis
            </button>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                {['N° Devis', 'Date', 'Client', 'Commercial(e)', 'Montant TTC', 'Statut'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#374151' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...quotes].filter(q => q.statut !== 'archive').reverse().slice(0, 8).map((q) => {
                const comm = profiles.find(p => p.id === q.user_id);
                const clientName = q.client_info?.clientName || q.client_info?.name || '—';
                const statutColors = { gagne: '#10b981', en_cours: '#f59e0b', perdu: '#ef4444' };
                const statutLabels = { gagne: 'Gagné', en_cours: 'En cours', perdu: 'Perdu' };
                return (
                  <tr key={q.id} style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background='white'}>
                    <td style={{ padding: '12px 14px', fontSize: '13px', fontWeight: 600, color: '#1e40af' }}>{q.quote_number}</td>
                    <td style={{ padding: '12px 14px', fontSize: '13px', color: '#374151' }}>{q.date ? q.date.split('-').reverse().join('/') : '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: '13px', color: '#374151' }}>{clientName}</td>
                    <td style={{ padding: '12px 14px', fontSize: '13px', color: '#374151' }}>{comm?.nom || '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: '13px', fontWeight: 600 }}>{fmt(q.total_with_vat)} FCFA</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ background: (statutColors[q.statut] || '#9ca3af') + '20', color: statutColors[q.statut] || '#9ca3af', border: `1px solid ${statutColors[q.statut] || '#9ca3af'}`, borderRadius: '999px', padding: '3px 10px', fontSize: '11px', fontWeight: 600 }}>
                        {statutLabels[q.statut] || q.statut}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </motion.div>
      </div>

      {/* Panneau Clients */}
      <div style={{ maxWidth: '1400px', margin: '0 auto 24px', padding: '0 32px', width: '100%' }}>
        <div style={{ background: 'white', borderRadius: '14px', padding: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <div style={{ fontWeight: 700, fontSize: '16px', marginBottom: '16px', color: '#111827' }}>👥 Base clients</div>
          <ClientsPanel isManager={true} />
        </div>
      </div>

      {/* Footer */}
      <footer style={{ background: '#1e3a7a', color: 'rgba(255,255,255,0.60)', textAlign: 'center', padding: '10px 24px', fontSize: '11px', letterSpacing: '0.02em', borderTop: '2px solid rgba(0,0,0,0.15)', marginTop: 'auto' }}>
        SolarQuotePro<sup style={{ fontSize: '8px', opacity: 0.5, verticalAlign: 'super' }}>TM</sup>
        {' '}by Takoussane Energy — v1.0.1 — © {new Date().getFullYear()}
      </footer>
      <ScrollToTop />
    </div>
  );
};

export default ManagerDashboard;
